import React, { useCallback, useRef, useState } from "react";

import {
  Accordion,
  AccordionSummary,
  Button,
  Chip,
  Collapse,
  Grid,
  Typography
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { isMobile } from "react-device-detect";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useStyles from "./Search.styles";
import {
  getSearchedGamesStart,
  resetSearchGames
} from "../../../../../../redux-saga/redux/casino/lobby/casinoGames";
import ProviderSection from "./ProviderSection";
import CategorySection from "./CategorySection";
import SearchField from "./SearchField";
import GamesSection from "./GamesSection";

const Search = ({ setShowSearch }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedProvider, setSelectedProvider] = useState({});
  const [selectedCategory, setSelectedCategory] = useState({});
  const { t } = useTranslation();
  const offset = useRef(1);

  const handleChange = (panel) => (e, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const makeProviderCategoryFilter = useCallback(() => {
    let filters = {};
    let providers = Object.keys(selectedProvider);
    let categories = Object.keys(selectedCategory);

    if (providers && providers.length) {
      filters.providerId = JSON.stringify(providers);
    }
    if (categories && categories.length) {
      filters.subCategoryId = JSON.stringify(categories);
    }
    return filters;
  }, [selectedProvider, selectedCategory]);

  const setProvider = useCallback((id, name) => {
    setSelectedProvider((current) => ({
      ...current,
      [id]: `provider,${id},${name}`
    }));
  }, []);

  const setCategory = useCallback((id, name) => {
    setSelectedCategory((current) => ({
      ...current,
      [id]: `category,${id},${name}`
    }));
  }, []);

  const setSearch = useCallback((searchValue) => {
    setSearchInput(searchValue);
  }, []);

  const removeSelectedItem = (selectedItem) => {
    offset.current = 1;
    const [type, id] = selectedItem.split(",");
    if (!type && !id) {
      return;
    }
    let filters = {
      isMobile,
      page: 1,
      limit: isMobile ? 8 : 12,
      search: searchInput.trim().length ? searchInput : null
    };
    let providers = selectedProvider;
    let categories = selectedCategory;

    if (type === "provider") {
      delete providers[id];
      setSelectedProvider((currentProviders) => {
        let providersTemp = currentProviders;
        delete providersTemp[id];
        return { ...providersTemp };
      });
    }
    if (type === "category") {
      delete categories[id];
      setSelectedCategory((currentCategories) => {
        let categoriesTemp = currentCategories;
        delete categoriesTemp[id];
        return { ...categoriesTemp };
      });
    }

    providers = Object.keys(providers);

    categories = Object.keys(categories);
    if (!providers.length && !categories.length && !searchInput.trim().length) {
      dispatch(resetSearchGames());
      return;
    }
    if (providers && providers.length) {
      filters.providerId = JSON.stringify(providers);
    }
    if (categories && categories.length) {
      filters.subCategoryId = JSON.stringify(categories);
    }

    dispatch(
      getSearchedGamesStart({
        ...filters
      })
    );
  };

  const getSelectedItems = () => {
    return [
      ...Object.values(selectedProvider),
      ...Object.values(selectedCategory)
    ].map((selectedItem) => (
      <Grid key={`${selectedItem}`} item>
        <Chip
          className={classes.selectemItemBtn}
          onDelete={() => removeSelectedItem(selectedItem)}
          size="medium"
          variant="outlined"
          label={selectedItem.split(",")[2]}
        />
      </Grid>
    ));
  };

  const resetFilters = () => {
    offset.current = 1;
    setSearchInput("");
    setSelectedProvider({});
    setSelectedCategory({});
    dispatch(resetSearchGames());
  };
  const location = useLocation();
  return (
    <div className={classes.searchContainer}>
      <Grid container>
        <Grid item xs={12} lg={3} className={classes.category}>
          <div className={classes.serachWrapper}>
            <Accordion
              expanded={expanded === "providers"}
              onChange={handleChange("providers")}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={expanded}
              >
                <Typography>{t("LOBBY.PROVIDERS")}</Typography>
              </AccordionSummary>
              <ProviderSection
                offset={offset}
                searchInput={searchInput}
                selectedCategory={selectedCategory}
                selectedProvider={selectedProvider}
                removeSelectedItem={removeSelectedItem}
                setProvider={setProvider}
              />
            </Accordion>
            <Accordion
              expanded={
                location.pathname === "/casino/casinoLive" ||
                location.pathname === "/casino/slotGame" ||
                expanded === "categories"
              }
              onChange={handleChange("categories")}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={expanded}
              >
                <Typography>{t("LOBBY.CATEGORIES")}</Typography>
              </AccordionSummary>
              <CategorySection
                offset={offset}
                searchInput={searchInput}
                selectedCategory={selectedCategory}
                selectedProvider={selectedProvider}
                removeSelectedItem={removeSelectedItem}
                setCategory={setCategory}
              />
            </Accordion>
          </div>
        </Grid>
        <Grid item xs={12} lg={9} className={classes.search}>
          <Grid justifyContent="center" spacing={2} container>
            <Grid item xl={10} xs={12}>
              <SearchField
                searchInput={searchInput}
                offset={offset}
                setSearch={setSearch}
                makeProviderCategoryFilter={makeProviderCategoryFilter}
              />
            </Grid>
            <Grid item xl={2} xs={12}>
              <Button onClick={resetFilters} className={classes.btnPrimary}>
                {t("COMMON.RESET")}
              </Button>
            </Grid>
          </Grid>
          <Collapse
            in={
              !!(
                Object.keys(selectedCategory).length ||
                Object.keys(selectedProvider).length
              )
            }
          >
            <Grid spacing={2} container>
              {getSelectedItems()}
            </Grid>
          </Collapse>
          <Grid className={classes.modalGame}>
            <GamesSection
              selectedCategory={selectedCategory}
              selectedProvider={selectedProvider}
              searchInput={searchInput}
              offset={offset}
              makeProviderCategoryFilter={makeProviderCategoryFilter}
              setShowSearch={setShowSearch}
            />
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Search;
